import { AccountInfo } from "@azure/msal-browser";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../../app";
import { UserProps } from "../../types";
import { formatDateTimestamp } from "../../intl";
import { sendMixpanelData } from "../../utils";
import { getUserInfo } from "../../services/user/userService";

export interface ApplicationState {
  user: UserProps | null;
  token: string | null;
  isLoadingUserData: boolean;
}

let fetchUserDataInFlight = false;
let fetchUserDataSessionVersion = 0;

export const applicationSlice = createSlice({
  name: "application",

  initialState: {
    user: null,
    token: null,
    isLoadingUserData: false,
  } as ApplicationState,

  reducers: {
    setUserData: (state, action) => {
      state.user = {
        ...state.user,
        ...action.payload,
      };
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
    },
    setIsLoadingUserData: (state, action) => {
      state.isLoadingUserData = action.payload;
    },
    clearUserData: (state) => {
      state.user = null;
      state.token = null;
      state.isLoadingUserData = false;
      fetchUserDataInFlight = false;
      fetchUserDataSessionVersion += 1;
    },
  },
});

const fetchUserData = createAsyncThunk(
  "application/fetchUserData",
  async (
    msalUserData: AccountInfo | undefined,
    { dispatch, getState, signal },
  ) => {
    const state = getState() as RootState;
    const requestToken = state.application.token;
    const requestSessionVersion = fetchUserDataSessionVersion;
    dispatch(applicationSlice.actions.setIsLoadingUserData(true));

    try {
      const { token, user } = state?.application ?? {};
      if (!token) throw new Error("No token found");

      const userData = await getUserInfo();
      const latestState = getState() as RootState;
      if (
        signal.aborted ||
        fetchUserDataSessionVersion !== requestSessionVersion ||
        latestState.application.token !== requestToken
      ) {
        return;
      }
      dispatch(
        applicationSlice.actions.setUserData({
          ...user,
          ...msalUserData,
          ...userData,
        }),
      );

      if (import.meta.env.VITE_ENVIRONMENT === "production") {
        sendMixpanelData("Login", {
          email: userData?.mail,
          date: formatDateTimestamp(new Date()),
        });
      }
    } finally {
      fetchUserDataInFlight = false;
      dispatch(applicationSlice.actions.setIsLoadingUserData(false));
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      const { token, user } = state.application;

      if (!token || user?.user_id || fetchUserDataInFlight) {
        return false;
      }

      fetchUserDataInFlight = true;
      return true;
    },
    dispatchConditionRejection: true,
  },
);

export const actions = {
  ...applicationSlice.actions,
};

export const thunks = {
  fetchUserData,
};
